import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const TEMPLATE_COLUMNS = [
  { title: 'Component Name', description: 'e.g., Foundation, External Walls, Roof Structure' },
  { title: 'Category', description: 'Structure, Envelope, MEP, Interiors, Site, etc.' },
  { title: 'GWP', description: 'Global Warming Potential (kg CO₂-eq)' },
  { title: 'AP', description: 'Acidification Potential (kg SO₂-eq)' },
  { title: 'EP', description: 'Eutrophication Potential (kg PO₄-eq)' },
  { title: 'ODP', description: 'Ozone Depletion Potential (kg CFC-11-eq)' },
];

const SAMPLE_ROWS = [
  { name: 'Foundation', category: 'Structure', gwp: '45,200', ap: '285', ep: '62', odp: '0.012' },
  { name: 'External Walls', category: 'Envelope', gwp: '38,500', ap: '242', ep: '51', odp: '0.009' },
  { name: 'Roof Structure', category: 'Envelope', gwp: '22,800', ap: '145', ep: '28', odp: '0.006' },
];

const TEMPLATE_NOTES = [
  'Keep the first row reserved for column headers.',
  'Component name is required for every row.',
  'Category is optional — we attempt to infer it if not supplied.',
  'Impact values should be numeric (decimals allowed).',
  'Leave a cell blank if you do not have data for a specific impact.',
  'Supported formats: .xlsx, .xls, and .csv.',
  'Maximum file size: 10 MB.',
];

const RHINO_REQUIREMENTS = [
  { text: 'Rhino with the EmbodiX plugin installed (Download from the Package Manager or Food4Rhino).' },
  {
    text: 'A Rhino model organized into layers (each layer becomes one row). Check the ',
    linkText: 'Export from Rhino',
    linkHref: '#rhino-export',
    tail: ' section for more details on how to organize your model for a clean export.'
  },
  { text: 'An exported .embodix file from the EmbodiX plugin.' }
];

const RHINO_WORKFLOW_STEPS = [
  {
    title: 'Upload the .embodix file',
    description: {
      prefix:
        'Start page -> Rhino Integration. Upload your .embodix export and confirm the filename is shown. If the file format or content is wrong you will see an error. Use ',
      linkText: 'Export from Rhino',
      linkHref: '#rhino-export',
      suffix: ' section in this Documentation page if you need help, then click Start to open the LCA tool and load the Rhino data.'
    }
  },
  {
    title: 'Match layers to LCA / Building Component categories',
    description:
      'Layer Matcher is the bridge between your Rhino layers and the LCA categories. It helps you place each layer into Structure, Envelope, Design, or Mechanical before anything appears in the \'Build\' tab. The tab lists layer name, declared unit, and quantity from Rhino. Use the \'Category\' dropdown to assign the correct category for each layer, then click Save to store the mapping and populate the Build tab. Unassigned layers are skipped and flagged.'
  },
  {
    title: 'Select EPDs',
    description:
      'Collect Data is your EPD library. Search, filter, and shortlist EPDs you plan to use. Create your own EPDs here if you cannot find one in the list. This is also where you create and save wall buildups (in the Wall Buildups section). Saved buildups appear in the Build tab so you can reuse them quickly instead of re-entering layers.'
  },
  {
    title: 'Complete the Build tab',
    description:
      'The Build tab is where you turn your layers into full LCA components. Each Rhino layer appears as a row. Layer name, quantity, and unit are locked to Rhino values. Select an EPD for each layer, review service life, and use Component actions -> A4 impact to add transport distance and legs. Add density, thickness, or custom factor only when conversion requires it. Use \'Add component\' to add more categories, or insert saved wall buildups where they make sense.'
  },
  {
    title: 'Review Results and dashboard charts',
    description:
      'The Results tab becomes active once at least one layer is fully populated. Review totals, breakdowns, and stage impacts here. For dashboard summaries, complete every layer so all charts, pie charts, and life-cycle stage bars reflect computed values only.'
  }
];

const RHINO_LAYER_MATCHER_NOTES = [
  'Layer name, declared unit, and quantity come from the export.',
  'Assign a category to route each layer into Structure, Envelope, Design, or Mechanical.',
  'Unassigned layers are skipped and a warning is shown on Save.'
];

const RHINO_BUILD_NOTES = [
  'Layer name, quantity, and unit stay locked to Rhino values.',
  'Use Component actions -> A4 impact to add transport impacts.',
  'Results and charts appear only when every layer has valid data.'
];

const RHINO_TROUBLESHOOTING = [
  'Upload rejected: only .embodix files are accepted.',
  'Results disabled: check that every layer has a category and an EPD.',
  'Missing A4 impact: provide distance and legs, and add density/thickness/custom factor if conversion is required.'
];

const RHINO_FILE_FIELDS = [
  { field: 'layer', meaning: 'Rhino layer name (name of the building component)' },
  { field: 'type', meaning: 'Object type (surface, curve, point, etc.)' },
  { field: 'declaration', meaning: 'Declared unit (Volume, Area, Length, Count)' },
  { field: 'quantity', meaning: 'Numeric quantity of the declared units from the model' }
];

const RHINO_DECLARATION_MAP = [
  { declaration: 'Volume', unit: 'm3' },
  { declaration: 'Area', unit: 'm2' },
  { declaration: 'Length', unit: 'm' },
  { declaration: 'Count', unit: 'pcs' }
];

const RHINO_GLOSSARY = [
  { term: 'LCA', meaning: 'Life Cycle Assessment - the full impact review across product and building stages.' },
  { term: 'EPD', meaning: 'Environmental Product Declaration - a product data sheet used for impact calculations.' },
  { term: 'Declared unit', meaning: 'The unit used by an EPD, such as m3, m2, kg, or pcs.' },
  { term: 'Service life', meaning: 'Expected lifespan of a component in years, used for replacements.' },
  { term: 'GWP', meaning: 'Global Warming Potential - the carbon impact shown in charts.' },
  { term: 'Wall buildup', meaning: 'A saved assembly of layers and EPDs that can be reused in Build.' },
  { term: 'Component', meaning: 'A row in the Build tab representing a material or system with an assigned EPD.' }
];

const DASHBOARD_LINKS = [
                        ['Overview', '#dashboard-overview'],
  ['Breakdown', '#dashboard-breakdown'],
  ['Data visualisation', '#dashboard-data-visualisation'],
  ['ROI (Return on Investment)', '#dashboard-roi'],
  ['Buildups and components', '#dashboard-buildups'],
  ['LCA', '#dashboard-lca']
];

const RHINO_LINKS = [
  ['Requirements', '#rhino-requirements'],
  ['File structure', '#rhino-file'],
  ['Export from Rhino', '#rhino-export'],
  ['Layer matcher', '#rhino-layer-matcher'],
  ['Build and results', '#rhino-build'],
  ['Workflow', '#rhino-workflow'],
  ['Troubleshooting', '#rhino-troubleshooting']
];

const EXCEL_LINKS = [
  ['Required columns', '#excel-columns'],
  ['Example rows', '#excel-rows'],
  ['Important notes', '#excel-notes'],
  ['Download', '#excel-download']
];

const GLOSSARY_LINKS = [['Glossary', '#global-glossary']];

const NAV_SECTIONS = [
  { id: 'dashboard', label: 'Dashboard Tabs', links: DASHBOARD_LINKS },
  { id: 'rhino', label: 'Rhino integration', links: RHINO_LINKS },
  { id: 'excel', label: 'Excel template', links: EXCEL_LINKS }
];

const DocCard = ({ children }) => (
  <div className="rounded-2xl border border-[#d5dacc] bg-white px-6 py-6 shadow-sm">
    {children}
  </div>
);

const SimpleList = ({ items, className }) => (
  <ul className={className}>
    {items.map((item, idx) => (
      <li key={`${item}-${idx}`}>{item}</li>
    ))}
  </ul>
);

const DocumentationPage = () => {
  const [activeDoc, setActiveDoc] = useState('about');
  const [openSections, setOpenSections] = useState({
    dashboard: false,
    rhino: false,
    excel: false
  });
  const flowGridRef = useRef(null);
  const createOwnRef = useRef(null);
  const shortlistRef = useRef(null);
  const buildupsRef = useRef(null);
  const useLcaRef = useRef(null);

  const [flowPaths, setFlowPaths] = useState({ a: '', b: '' });

  const mainBoxClass = 'rounded-lg border border-[#d5dacc] bg-white px-3 py-2 text-center relative z-10';
  const dashedBoxClass = 'rounded-lg border border-dashed border-[#c2c9be] bg-[#f1f4ec] px-3 py-2 text-center text-black';
  const arrowCellClass = 'flex items-center justify-center relative z-10';

  const InlineArrow = ({ id, colStart }) => (
    <div
      className={`row-start-1 ${arrowCellClass}`}
      style={{ gridColumnStart: colStart }}
    >
      <svg className="h-3 w-full overflow-visible" viewBox="0 0 100 10" aria-hidden="true">
        <defs>
          <marker id={id} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#4f6b5a" />
          </marker>
        </defs>
        <line
          x1="-12"
          y1="5"
          x2="112"
          y2="5"
          stroke="#4f6b5a"
          strokeWidth="4"
          markerEnd={`url(#${id})`}
        />
      </svg>
    </div>
  );

  useEffect(() => {
    const update = () => {
      const gridEl = flowGridRef.current;
      const createEl = createOwnRef.current;
      const shortEl = shortlistRef.current;
      const buildEl = buildupsRef.current;
      const lcaEl = useLcaRef.current;

      if (!gridEl || !createEl || !shortEl || !buildEl || !lcaEl) return;

      const gridRect = gridEl.getBoundingClientRect();
      const cRect = createEl.getBoundingClientRect();
      const sRect = shortEl.getBoundingClientRect();
      const bRect = buildEl.getBoundingClientRect();
      const lRect = lcaEl.getBoundingClientRect();

      // Helper to convert viewport coords to grid local coords.
      const toLocal = (x, y) => ({
        x: x - gridRect.left,
        y: y - gridRect.top
      });

      // Arrow A: Create own EPD (right center) -> Shortlist materials (bottom center)
      const aStart = toLocal(cRect.right, (cRect.top + cRect.bottom) / 2);
      const aEnd = toLocal((sRect.left + sRect.right) / 2, sRect.bottom);

      // Stepped route with a single bevel at the turn (no bump)
      const bevel = 6;
      const aP1 = { x: aStart.x + 18, y: aStart.y };
      const aCorner = { x: aEnd.x, y: aStart.y };

      const pathA =
        `M ${aStart.x} ${aStart.y}` +
        ` L ${aP1.x} ${aP1.y}` +
        ` L ${aCorner.x - bevel} ${aCorner.y}` +
        ` L ${aCorner.x} ${aCorner.y - bevel}` +
        ` L ${aEnd.x} ${aEnd.y}`;

      // Arrow B: Create wall buildups (bottom center) -> Use wall sections (left center)
      const bStart = toLocal((bRect.left + bRect.right) / 2, bRect.bottom);
      const bEnd = toLocal(lRect.left, (lRect.top + lRect.bottom) / 2);

      const bP1 = { x: bStart.x, y: bEnd.y };

      const pathB =
        `M ${bStart.x} ${bStart.y}` +
        ` L ${bP1.x} ${bP1.y - bevel}` +
        ` L ${bP1.x + bevel} ${bP1.y}` +
        ` L ${bEnd.x} ${bEnd.y}`;

      setFlowPaths({ a: pathA, b: pathB });
    };

    if (activeDoc !== 'dashboard') return;

    // Run after the dashboard content mounts
    const raf1 = requestAnimationFrame(() => {
      update();
      // Second frame helps mobile browsers after layout settles
      requestAnimationFrame(update);
    });

    window.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(raf1);
      window.removeEventListener('resize', update);
    };
  }, [activeDoc]);

  const handleDocToggle = (doc) => {
    if (activeDoc !== doc) {
      setActiveDoc(doc);
      setOpenSections((prev) => ({ ...prev, [doc]: true }));
      return;
    }
    setOpenSections((prev) => ({ ...prev, [doc]: !prev[doc] }));
  };

  return (
    <div className="min-h-screen h-screen overflow-y-auto visible-scrollbar bg-[#e9ede3] px-4 py-8 text-black">
      <div className="mx-auto flex w-full max-w-none flex-col gap-2 sm:gap-6">
        <div className="flex flex-col items-center gap-2 sm:gap-3 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:h-16">
          <Link to="/dashboard" className="hidden justify-self-start lg:self-center text-sm leading-none text-[#6b7d6f] hover:text-[#2f6046] lg:inline-flex">
            ← Back to dashboard
          </Link>
          <div className="text-center w-full lg:self-center">
            <h1 className="text-[clamp(1.8rem,7vw,2.25rem)] sm:text-4xl font-black leading-none whitespace-nowrap tracking-tight drop-shadow-[0_0_1px_rgba(43,67,49,0.55)] text-[#2f6046]">Welcome to EmbodiX Docs!</h1>
          </div>
          <div aria-hidden="true" />
        </div>

        <Link
          to="/dashboard"
          className="mt-2 mb-0 inline-flex items-center justify-center text-sm text-[#6b7d6f] hover:text-[#2f6046] lg:hidden"
        >
          ← Back to dashboard
        </Link>

        <div className="flex flex-col gap-3 sm:gap-6 lg:flex-row">
          <aside className="w-full shrink-0 lg:w-56 lg:sticky lg:top-0 lg:self-start lg:h-fit">
            <div className="rounded-2xl border border-[#d5dacc] bg-white shadow-sm">
              <div className="px-4 py-4">
                <p className="text-sm font-semibold text-black text-center">DOCS CONTENTS</p>
              </div>
              <div className="border-t border-[#d5dacc] px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => setActiveDoc('about')}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-1 text-left text-sm ${
                    activeDoc === 'about'
                      ? 'bg-[#eef2e7] text-black font-semibold'
                      : 'text-black font-medium'
                  }`}
                >
                  <span>About EmbodiX</span>
                </button>
              </div>
              {NAV_SECTIONS.map((section) => (
                <div key={section.id} className="border-t border-[#d5dacc] px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => handleDocToggle(section.id)}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-1 text-left text-sm ${
                      activeDoc === section.id
                        ? 'bg-[#eef2e7] text-black font-semibold'
                        : 'text-black font-medium'
                    }`}
                    aria-expanded={openSections[section.id]}
                  >
                    <span>{section.label}</span>
                    <span className={`inline-block text-xs transition-transform ${openSections[section.id] ? 'rotate-90' : ''}`}>
                      {'>'}
                    </span>
                  </button>
                  {openSections[section.id] && (
                    <div className="mt-1 px-2 text-sm text-black">
                    <div className="space-y-2">
                        {section.links.map(([label, href]) => (
                          <a
                            key={href}
                            href={href}
                            onClick={() => {
                              setActiveDoc(section.id);
                              setOpenSections((prev) => ({ ...prev, [section.id]: true }));
                            }}
                            className="block rounded-lg px-2 py-1 hover:bg-[#eef2e7]"
                          >
                            {label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="border-t border-[#d5dacc] px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => setActiveDoc('glossary')}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-1 text-left text-sm ${
                    activeDoc === 'glossary'
                      ? 'bg-[#eef2e7] text-black font-semibold'
                      : 'text-black font-medium'
                  }`}
                >
                  <span>Glossary</span>
                </button>
              </div>
            </div>
          </aside>

          <div className="flex-1">
            {activeDoc === 'about' ? (
              <DocCard>
                <section id="about-embodix" className="space-y-3">
                  <h2 className="text-2xl lg:text-3xl font-semibold text-black">About EmbodiX</h2>
                  <p className="text-base text-black text-justify">
                    EmbodiX aims to act as a catalyst in bridging sustainability analytics with the early design phase 
                    for architects. The tool enables Life Cycle Analysis (LCA) or carbon analysis integration easy from 
                    initial design decisions through to whole building LCA, shifting the focus from compliance toward design driven 
                    optimisation. The tool is designed especially for professionals with no prior exposure to 
                    sustainability analytics, presenting the process as an exploratory, puzzle like approach to design 
                    options and simplifying the complex analytics, while also offering detailed analytics for those who 
                    wish to explore further.
                  </p>
                  <p className="text-base text-black text-justify">
                    Positioned to support the design process rather than compliance, the tool focuses on translating the
                    international carbon targets and norms into scalable spatial design metrics that are aligned with 
                    the project’s country specific resource availability, national carbon budgets, and neutrality goals. In essence,
                    the tool is aimed to guide architects to design within the Planetary Boundaries, in line with 
                    construction capacity and resource availability at the project’s country, aligning with the country’s 
                    Net Zero carbon target and the 1.5 degree Paris limit.
                  </p>
                  <p className="text-base text-black text-justify">
                    <span className="inline-block w-full rounded-md border-dashed border border-[#d0473c] px-3 py-2 text-[#7d3614]">
                      Disclaimer: Please note that the tool is currently in beta version and is being tested for workability. This tool is not intended for
                      commercial use at the moment, but you are welcome to explore and share your comments. Check the Terms and Conditions page for more details.
                    </span>
                  </p>
                </section>

                <section id="about-embodix" className="space-y-3 mt-8">
                  <h2 className="text-2xl lg:text-3xl font-semibold text-black">What to expect in EmbodiX?</h2>
                  <p className="text-base text-black text-justify">
                    <span className="font-bold">Localizing targets:</span> Translate international carbon targets and norms 
                    into project country specific benchmarks to ensure the design remains relevant to local resource 
                    availability.
                  </p>
                  <p className="text-base text-black text-justify">
                    <span className="font-bold">Plug and play:</span> Puzzle like experimentation process for early design 
                    optimisations of wall and facade assemblies and PV integration, extending through detailed whole 
                    building LCA analysis for those who wish to push the boundary further.
                  </p>
                  <p className="text-base text-black text-justify">
                    <span className="font-bold">Visualization:</span> Explore your design impact through environmental 
                    analogies that help contextualise the equivalent environmental damage your project or a design may cause.
                  </p>
                  <p className="text-base text-black text-justify">
                    <span className="font-bold">Track and benchmark:</span> Understand and track design changes using 
                    multiple datasets throughout the design process, and benchmark performance against National and 
                    International targets to evaluate how the design performs in real world conditions.
                  </p>
                  <p className="text-base text-black text-justify">
                    <span className="font-bold">Interactive results:</span> Use interactive graphs and charts to trace 
                    impacts down to the individual building component level, enabling informed design decisions.
                  </p>
                </section>
                <section className="space-y-3 mt-8">
                  <h2 className="text-2xl lg:text-3xl font-semibold text-black">Methodologies and datasets</h2>
                  <p className="text-base text-black text-justify">
                    EmbodiX brings together EN 15804 aligned LCA methodologies and curated EPD datasets so each layer 
                    follows standardized impact accounting. You can plug in regional benchmarks, life-cycle stage 
                    weightings, and trace datasets back to their raw EPD sources, creating a transparent audit trail.
                  </p>
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-black -mb-2">Datasets</h3>
                    <p className="text-base text-black">
                      Embodix utilises EPD datasets from multiple sources including{' '}
                      <a className="text-[#2f6046] underline underline-offset-2" href="https://www.oekobaudat.de/no_cache/en/database/search.html" target="_blank" rel="noreferrer">OEKOBAUDAT</a>,{' '}
                      <a className="text-[#2f6046] underline underline-offset-2" href="https://app.2050-materials.com/" target="_blank" rel="noreferrer">2050 Materials</a>,{' '}
                      <a className="text-[#2f6046] underline underline-offset-2" href="https://www.environdec.com/library" target="_blank" rel="noreferrer">EPD International (in progress)</a>, and{' '}
                      <a className="text-[#2f6046] underline underline-offset-2" href="https://www.open-epd-forum.org/" target="_blank" rel="noreferrer">OpenEPD</a>.
                    </p>
                    </div>
                    <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-black -mb-2">Methodologies</h3>
                    <p className="text-base text-black text-justify">
                    Embodix also draws on methodologies referenced from other institutions for establishing carbon targets.
                    </p>
                    <p className="text-base text-black">
                      1.5 degree Paris limit targets for carbon and energy:{' '}
                      <a className="text-[#2f6046] underline underline-offset-2" href="https://crrem.org/crrem-risk-assessment-tool/" target="_blank" rel="noreferrer">
                        CRREM Risk Assessment Tool
                      </a>
                      <br />
                      Grid Intensity and Electricity Mix:{' '}
                      <a className="text-[#2f6046] underline underline-offset-2" href="https://ourworldindata.org/" target="_blank" rel="noreferrer">
                        Our world in data
                      </a>
                      <br />
                      <a className="text-base text-black">
                    Construction Capacity: Inspired from a tool by{' '} 
                      </a>
                    <a className="text-[#2f6046] underline underline-offset-2" href="https://www.appsheet.com/start/09a210d6-f94c-4827-bcd4-cf77f969192a?platform=desktop#appName=CONSTRUCTIONCAPACITYcalculator-973420253-24-03-27-3&vss=H4sIAAAAAAAAA6XOsQ7CIBQF0H-5M1_AahyMUQeNizhgeSRECk2h1Ybw74LaODeOXN657yWMhh7HKJs7-CX9XluawJEETlNHAlxg5V3svRVgAnvZfsLDSH01Ahn5ymYfKYCnZZz_t53BKHLRaEN97aqydHxd-a6qBLNBZmiHKG-W3gcXk3PJtG-GQOpcR5afEDZu_eykUzuvSqWWNlB-Aa_9LKVlAQAA&view=Overview" target="_blank" rel="noreferrer">Tomislav Martinović</a>{' '}
                    </p>
                    </div>
                </section>
                  
              </DocCard>
            ) : activeDoc === 'dashboard' ? (
              <DocCard>
                <section id="dashboard" className="space-y-3">
                  <h2 className="text-2xl font-semibold text-black text-center">Dashboard Tabs</h2>
                  <p className="text-sm text-black text-justify">
                    This section summarises all the tabs present in the dashboard and its functions while giving a clear
                    explanation on how to use these features in order to calculate the carbon impact of your design.
                  </p>
                </section>
                <div className="mt-6 space-y-8">
                  <section id="dashboard-overview" className="space-y-3">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Overview</h3>
                    <p className="text-sm text-black text-justify">
                      The Overview tab gives a quick read of the project: key totals at the top, then simple charts that
                      show which categories and materials drive the impact and how it is distributed across life-cycle
                      stages.
                    </p>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                      <li>Top cards summarize total impact, carbon intensity per m2, and the largest contributing component.</li>
                      <li>An interactive category pie chart helps you compare Structure, Envelope, Design, MEP, and other groups.</li>
                      <li>A comparison table highlights which material types or the building components contribute most.</li>
                      <li>Life-cycle bar chart shows where impacts sit across various LCA stages, be it manufacturing, transport or end use.</li>
                    </ul>
                  </section>
                  <div className="border-t border-[#d5dacc]" />
                  <section id="dashboard-breakdown" className="space-y-3">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Breakdown</h3>
                    <p className="text-sm text-black text-justify">
                      The Breakdown tab is a repository of the carbon impacts forumulated with an interactive table. This helps to view helps you see where the impact is coming from. Switch between categories like
                      Structure, Envelope, Finishes, Design and MEP, or even individual components to quickly identify the
                      building component which are biggest contributors to carbon impact and focus your design effort where it matters most.
                    </p>
                    </section>
                  <div className="border-t border-[#d5dacc]" />
                  <section id="dashboard-data-visualisation" className="space-y-3">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Data visualisation</h3>
                    <p className="text-sm text-black text-justify">
                      The Data Visualization tab combines a heat matrix and a Sankey flow diagram. Both views update
                      based on the selected impact metric (for example, GWP), so you can see where impacts are
                      concentrated and how they move through the model.
                    </p>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">Heat matrix chart</h4>
                      <p className="text-sm text-black text-justify">
                        The heat matrix plots components as rows and life cycle stages as columns. Each cell shows the
                        magnitude of impact for a building component of an LCA stage. Orange shades indicate higher
                        impacts while green indicates benefits or negative impacts.
                        By default you see grouped stages (A1-A3), and When detailed data is
                        present, you can expand the matrix to show individual stages (A1, A2, A3).
                      </p>
                      <div className="space-y-1">
                        <h5 className="text-sm font-semibold text-black -mt-1">Chart options:</h5>
                        <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                          <li><strong>Expand map:</strong> switch between grouped stages and individual stages (only available when stage data exists).</li>
                          <li><strong>Stage visibility:</strong> hide or show individual stages after expanding the map.</li>
                          </ul>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">Sankey flow diagram</h4>
                      <p className="text-sm text-black text-justify">
                        The interactive Sankey diagram shows how the impact flows through your project data. Ultimately, it is an interactive
                        way to understand the highest contributor in conjunction with multiple impact representations.
                        Nodes represent the building, categories, components, materials, and life cycle stage groups. Links/lines
                        are weighted by impact value, so thicker paths indicate larger contributions. The default view
                        focuses on Categories to LCA Stages when stage data is available, but you can enable or hide
                        other columns to match the story you want to tell.
                      </p>

                      <div className="space-y-2">
                        <h5 className="text-base font-semibold text-black mt-6">How to read the Sankey</h5>
                        <ol className="list-decimal space-y-1 pl-5 text-sm text-black text-left">
                          <li>Start at the leftmost column and follow the links to the right.</li>
                          <li>Use link thickness to compare contributions for the selected impact.</li>
                          <li>Hover a node to see totals and details, and hover links to see their names.</li>
                          <li>Click a node to highlight only its connected flows; click again to clear.</li>
                        </ol>
                      </div>

                      <div className="space-y-1">
                        <h5 className="text-sm font-semibold text-black -mt-1">Diagram options:</h5>
                        <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                          <li><strong>Edit diagram components:</strong> toggle Building, Categories, Components, Materials, and LCA Stages.</li>
                          <li><strong>Aggregate results:</strong> group links with the same inflow and outflow to reduce clutter (Excel uploads only).</li>
                          </ul>
                      </div>

                    </div>
                  </section>
                  <div className="border-t border-[#d5dacc]" />

                  <section id="dashboard-roi" className="space-y-3">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">ROI (Return on Investment)</h3>
                    <p className="text-sm text-black text-justify">
                      The ROI tab currently focuses on PV Carbon ROI. It explains how long a PV system takes to pay back
                      its own embodied carbon by offsetting grid electricity. The intent is to compare design options and
                      communicate carbon payback to see if the offset can be done before the service life of the pv, in other
                      words does it even make sense to include pv in design first.
                    </p>
                    <p className="text-sm text-black text-justify">
                      The offset should be done before the PV's total service life, or else it is not recommended to invest PV for
                      that location. To experiment in detail, make sure to add future decarbonization grid intensity values to 
                      all analysis years manually for accurate analysis.
                    </p>
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-2">
                        <h4 className="text-base font-semibold text-black leading-snug mt-2">
                          <span className="block sm:inline">Inputs needed for payback</span>{' '}
                          <span className="block sm:inline">calculation:</span>
                        </h4>
                        <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                          <li>PV embodied carbon: total upfront impact for the PV system (panels + any other items you include).</li>
                          <li>PV Panel efficiency: the rated efficiency used to estimate annual output.</li>
                          <li>Solar radiation: the annual solar radiation (kWh/m2/year)for the project location.</li>
                          <li>Roof coverage: the portion of roof area allocated to PV.</li>
                          <li>Degradation rate: yearly performance loss for the PV system.</li>
                          <li>Grid intensity: the current grid carbon intensity for the location.</li>
                          <li>Grid decarbonization (optional): year-by-year grid intensity values if you want a future scenario.</li>
                          <li>PV service life: the analysis period to compare payback against system life.</li>
                        </ul>
                      </div>
                      <div className="rounded-xl border border-black bg-white overflow-hidden px-1 py-1 sm:px-1 sm:py-1">
                      <div className="flex flex-wrap items-baseline gap-1 text-[10px] sm:text-sm text-black mb-1 leading-snug px-1 pt-1 sm:px-1 sm:pt-1">
                        <span className="font-semibold text-black">Payback period:</span>
                        <span className="break-words">
                          The year when the cumulative upfront impact has been offset.
                        </span>
                      </div>
                        <div className="mt-0 md:mt-0.5 mb-0">
                          <svg viewBox="0 0 420 220" className="h-44 md:h-56 w-full max-w-full" preserveAspectRatio="xMidYMid meet">
                            <rect x="0" y="0" width="420" height="220" fill="#ffffff" />
                            <defs>
                              <pattern id="paybackHatch" patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
                                <line x1="0" y1="0" x2="0" y2="5" stroke="#b91c1c" strokeWidth="1.1" opacity="0.5" />
                              </pattern>
                              <linearGradient id="savingsShade" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#2f6046" stopOpacity="0.18" />
                                <stop offset="100%" stopColor="#2f6046" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <line x1="40" y1="120" x2="400" y2="120" stroke="#c2c9be" strokeWidth="2" />
                            <line x1="40" y1="24" x2="40" y2="190" stroke="#e1e5da" strokeWidth="2" />
                            <path d="M40 34 L75 52 L105 66 L135 78 L165 92 L200 108 L252 120" fill="none" stroke="none" />
                            <path d="M40 34 L75 52 L105 66 L135 78 L165 92 L200 108 L252 120 L252 120 L40 120 Z" fill="url(#paybackHatch)" />
                            <path d="M252 120 L285 130 L315 144 L350 160 L380 170 L400 178 L400 120 L252 120 Z" fill="url(#savingsShade)" />
                            <path d="M40 34 L75 52 L105 66 L135 78 L165 92 L200 108 L252 120 L285 130 L315 144 L350 160 L380 170 L400 178" fill="none" stroke="#2f6046" strokeWidth="2.5" />
                            <circle cx="252" cy="120" r="4" fill="#2f6046" />
                            <line x1="252" y1="48" x2="252" y2="190" stroke="#2f6046" strokeWidth="1" strokeDasharray="3 4" />
                            <text x="252" y="30" textAnchor="middle" fontSize="12" fill="#2f6046" className="sm:hidden">Payback (year 12)</text>
                            <text x="252" y="30" textAnchor="middle" fontSize="12" fill="#2f6046" className="hidden sm:block">Payback (year 12)</text>
                            <text
                              x="18"
                              y="170"
                              fontSize="12"
                              fill="#6b7d6f"
                              transform="rotate(-90 18 160)"
                              className="sm:hidden"
                            >
                              Net impact (kg CO2e)
                            </text>
                            <text
                              x="18"
                              y="170"
                              fontSize="11"
                              fill="#6b7d6f"
                              transform="rotate(-90 18 160)"
                              className="hidden sm:block"
                            >
                              Net impact (kg CO2e)
                            </text>
                            <text x="300" y="115" fontSize="12" fill="#6b7d6f" className="sm:hidden">PV service years</text>
                            <text x="310" y="115" fontSize="11" fill="#6b7d6f" className="hidden sm:block">PV service years</text>
                            <text x="52" y="30" fontSize="12" fill="#b91c1c" className="sm:hidden">Upfront impact</text>
                            <text x="52" y="30" fontSize="11" fill="#b91c1c" className="hidden sm:block">Upfront impact</text>
                            <text x="380" y="192" fontSize="12" fill="#16a34a" textAnchor="end" className="sm:hidden">Savings over time</text>
                            <text x="380" y="192" fontSize="11" fill="#16a34a" textAnchor="end" className="hidden sm:block">Savings over time</text>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">How payback years are calculated (simple)</h4>
                      <p className="text-sm text-black text-justify">
                        Think of the PV system as starting with a carbon “debt” (its upfront impact). Each year, the
                        panels make clean electricity, so you “pay back” some of that debt by avoiding dirty grid
                        electricity.
                      </p>
                      <ol className="list-decimal space-y-1 pl-5 text-sm text-black text-left">
                        <li>
                          We estimate how much electricity the PV makes each year:
                          <strong> AnnualElectricity = AnnualSolar x (RoofCoverage %) x (PanelEfficiency %)</strong>.
                        </li>
                        <li>
                          We adjust performance for degradation each year (linear drop):
                          <strong> Performance(t) = 1 - (Degradation %) x t</strong> (t is the year number).
                        </li>
                        <li>
                          We turn that electricity into a carbon saving:
                          <strong> AnnualCarbonSaved(t) = AnnualElectricity x GridIntensity(t) x Performance(t)</strong>.
                        </li>
                        <li>
                          We add up the savings over time until they cancel the upfront impact:
                          <strong> CarbonBalance(t) = UpfrontImpact - Σ AnnualCarbonSaved(1..t)</strong>.
                          The payback year is the first t when CarbonBalance(t) reaches zero or below.
                        </li>
                      </ol>
                      <p className="text-sm text-black text-justify">
                        The first year when the running total reaches zero is the payback year. If that year is later
                        than the PV service life, the payback does not happen in time.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <h4 className="text-base font-semibold text-black mt-6">Step by step: get a PV carbon ROI result</h4>
                      <ol className="space-y-3">
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold leading-tight text-black">
                            Step 1
                          </div>
                          <div className="text-sm font-semibold text-black">Open the ROI module</div>
                          <p className="text-sm text-black text-justify">
                            Go to ROI and select <strong>PV Carbon ROI</strong> (the active module).
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 2
                          </div>
                          <div className="text-sm font-semibold text-black">Enter embodied carbon</div>
                          <p className="text-sm text-black text-justify">
                            Add PV panel embodied carbon and any other components (or tick "included above").
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 3
                          </div>
                          <div className="text-sm font-semibold text-black">Set energy inputs</div>
                          <p className="text-sm text-black text-justify">
                            Enter panel efficiency, annual solar radiation, roof coverage, and degradation rate.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 4
                          </div>
                          <div className="text-sm font-semibold text-black">Add grid intensity</div>
                          <p className="text-sm text-black text-justify">
                            Use <strong>Lookup grid carbon</strong> or type a value for grid intensity.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 5
                          </div>
                          <div className="text-sm font-semibold text-black">Optional: decarbonize the grid</div>
                          <p className="text-sm text-black text-justify">
                            Turn on <strong>Decarbonize</strong> if you want to edit a year-by-year grid intensity schedule.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 6
                          </div>
                          <div className="text-sm font-semibold text-black">Confirm service life</div>
                          <p className="text-sm text-black text-justify">
                            Check the PV service life (25-35 years) so the timeline matches your design intent.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 7
                          </div>
                          <div className="text-sm font-semibold text-black">Read the result</div>
                          <p className="text-sm text-black text-justify">
                            Review the table and graph. The break-even year is where the net line crosses zero. Export
                            the chart if you need it for reports.
                          </p>
                        </li>
                      </ol>
                    </div>
                  </section>
                  <div className="border-t border-[#d5dacc]" />
                  <section id="dashboard-buildups" className="space-y-3">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Buildups and components</h3>
                    <p className="text-sm text-black text-justify">
                      This tab is your assembly sandbox. It lets you build wall sections layer by layer, attach real EPD
                      datasets, and compare options on a simple, per m2 basis. You do not need sustainability training
                      to use it. Think of it as a way to compare design alternatives with clear numbers before you commit
                      to the full model.
                    </p>

                    <div className="rounded-xl border border-[#d5dacc] bg-[#f6f8f3] p-4">
                      <div className="text-base font-semibold text-black mb-3">
                        Steps for wall buildup analysis
                      </div>
                      <div className="text-sm text-black">
                        <div className="overflow-x-auto overflow-y-visible pb-2">
                          <div
                            ref={flowGridRef}
                            className="relative grid min-w-[820px] grid-cols-[1fr_34px_1fr_34px_1fr_34px_1fr_34px_1fr] grid-rows-[auto_auto] items-center gap-x-2 gap-y-1 pt-3 pb-3 overflow-visible"
                          >
                            {/* Main flow row */}
                            <div className={`row-start-1 col-start-1 ${mainBoxClass}`}>
                              Search EPD databases
                            </div>
                            <InlineArrow id="flowInlineArrow" colStart={2} />

                            <div
                              ref={shortlistRef}
                              className={`row-start-1 col-start-3 ${mainBoxClass}`}
                            >
                              Shortlist EPDs
                            </div>
                            <InlineArrow id="flowInlineArrow2" colStart={4} />

                            <div
                              ref={buildupsRef}
                              className={`row-start-1 col-start-5 ${mainBoxClass}`}
                            >
                              Create wall sections
                            </div>
                            <InlineArrow id="flowInlineArrow3" colStart={6} />

                            <div className={`row-start-1 col-start-7 ${mainBoxClass}`}>
                              Calculate impacts
                            </div>
                            <InlineArrow id="flowInlineArrow4" colStart={8} />

                            <div className={`row-start-1 col-start-9 ${mainBoxClass}`}>
                              Compare options
                            </div>

                            {/* Row 2 boxes aligned */}
                            <div className="row-start-2 col-start-1 mt-3 relative z-10">
                              <div ref={createOwnRef} className={dashedBoxClass}>
                                Create own EPD
                              </div>
                            </div>

                            <div className="row-start-1 row-end-3 col-start-1 pointer-events-none flex items-center justify-center text-center text-black relative z-20 -mt-3">
                              (or)
                            </div>

                            <div
                              ref={useLcaRef}
                              className={`row-start-2 col-start-7 mt-3 ${dashedBoxClass} relative z-10`}
                            >
                              Use created wall sections in the LCA tab
                            </div>

                            {/* Single overlay SVG in pixel coordinates (no stretching) */}
                            <svg
                              className="pointer-events-none absolute inset-0 h-full w-full z-50"
                              aria-hidden="true"
                            >
                              <defs>
                                <marker
                                  id="flowArrowHead"
                                  markerWidth="14"
                                  markerHeight="14"
                                  refX="12"
                                  refY="7"
                                  orient="auto"
                                  markerUnits="userSpaceOnUse"
                                >
                                  <path d="M0,0 L14,7 L0,14 Z" fill="#ff0000" />
                                </marker>
                              </defs>

                              {flowPaths.a ? (
                                <path
                                  d={flowPaths.a}
                                  fill="none"
                                  stroke="#ff0000"
                                  strokeWidth="3.5"
                                  strokeDasharray="1 4"
                                  markerEnd="url(#flowArrowHead)"
                                />
                              ) : null}

                              {flowPaths.b ? (
                                <path
                                  d={flowPaths.b}
                                  fill="none"
                                  stroke="#ff0000"
                                  strokeWidth="3.5"
                                  strokeDasharray="1 4"
                                  markerEnd="url(#flowArrowHead)"
                                />
                              ) : null}
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">What this tab offers</h4>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                        <li>Build wall sections as layered assemblies.</li>
                        <li>Attach EPD datasets to each layer and see a per m2 carbon result (GWP) of the wall assembly.</li>
                        <li>Design and compare multiple wall assembly options side by side with a clear best option indicator.</li>
                        <li>Save wall buildups so they appear in your library for quick reference later. These saved wall buildups can also be used for whole building lca analysis.</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">Where the EPD data comes from</h4>
                      <p className="text-sm text-black text-justify">
                        In Step 1 you can search real EPD databases and shortlist the materials you need. The explorer
                        connects to the following sources:
                      </p>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                        <li>
                          <a
                            href="https://www.oekobaudat.de/no_cache/en/database/search.html"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#2f6046] underline underline-offset-2"
                          >
                            OEKOBAUDAT
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://app.2050-materials.com/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#2f6046] underline underline-offset-2"
                          >
                            2050 Materials
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://www.environdec.com/library"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#2f6046] underline underline-offset-2"
                          >
                            EPD International / Environdec (soon)
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://www.open-epd-forum.org/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#2f6046] underline underline-offset-2"
                          >
                            OpenEPD
                          </a>
                        </li>
                        <li>Custom EPDs you create yourself</li>
                      </ul>
                      <p className="text-sm text-black text-justify">
                        When you shortlist an EPD, EmbodiX stores its impact information across life cycle stages
                        (A1-A3, A4, A5, B, C, D), and any material properties like density, thickness, or grammage in a shortlisted table. Custom
                        EPDs are also stored. These are locally stored in your browser so they persist after refresh.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-base font-semibold text-black mt-6">Step by step: Calculate wall section / buildup impacts</h4>
                      <p className="text-sm text-black text-justify">
                        This workflow is designed for non-technical users. You pick materials, stack them like layers in a
                        wall, and EmbodiX adds the impacts together for 1 m2 so you can compare options easily.
                      </p>
                      <ol className="space-y-3">
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 1
                          </div>
                          <div className="text-sm font-semibold text-black">Shortlist materials</div>
                          <p className="text-sm text-black text-justify">
                            Search the EPD database like a product catalog. When you find a material you want to use,
                            open it and click Shortlist. If the exact product is missing, create a custom EPD so you can
                            keep moving.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 2
                          </div>
                          <div className="text-sm font-semibold text-black">Design a wall section</div>
                          <p className="text-sm text-black text-justify">
                            Create a wall section that represents 1 m2. Give it a name, add layers in the correct order
                            (outside to inside), and set each layer thickness. The visual preview helps you confirm the
                            stack looks right.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 3
                          </div>
                          <div className="text-sm font-semibold text-black">Assign EPDs to layers</div>
                          <p className="text-sm text-black text-justify">
                            For each layer, choose the matching EPD from your shortlist. The table will tell you whether
                            the data already fits 1 m2 or if additional info is needed.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 4
                          </div>
                          <div className="text-sm font-semibold text-black">Adjustment factors</div>
                          <p className="text-sm text-black text-justify">
                            If the EPD is shown per m3 or per kg, enter thickness and density so EmbodiX can convert it
                            to 1 m2. Use the adjustment factor only when you need a small manual correction.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 5
                          </div>
                          <div className="text-sm font-semibold text-black">Save the wall buildup</div>
                          <p className="text-sm text-black text-justify">
                            Click Save info to store the total impact for that wall. Saved walls show up in the Created
                            wall buildups panel and can be reused later in the Whole Building LCA tab.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 6
                          </div>
                          <div className="text-sm font-semibold text-black">Compare results</div>
                          <p className="text-sm text-black text-justify">
                            Choose which life cycle stages to include (A, B, C, D), then compare totals across wall
                            options. The chart highlights the lowest-impact option so decisions are faster.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 7
                          </div>
                          <div className="text-sm font-semibold text-black">See stage detail (optional)</div>
                          <p className="text-sm text-black text-justify">
                            Turn on detailed results if you want to see A, B, C, and D stages side by side for each wall.
                            This helps you understand where the impact is coming from.
                          </p>
                        </li>
                      </ol>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">How the calculations work</h4>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                        <li>
                          The tab focuses on carbon impact (GWP, shown as kg CO2e) per 1 m2 of wall.
                        </li>
                        <li>
                          If an EPD is already per m2, EmbodiX uses it directly. if not the tool converts it to per m2 using density, thickness or grammage. if they
                        </li>
                        <li>
                          If material properties are missing, EmbodiX may use a generic density or grammage and flags it
                          in the UI so you can input your custom material propery details.
                        </li>
                        <li>
                          The total wall impact is the sum of all layers impacts for the selected life cycle stages. You can choose which stages to be considered for the calculation.
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">Where the results go</h4>
                      <p className="text-sm text-black text-justify">
                        Each saved wall stores its layers, selected stages, and total GWP per m2. These saved wall
                        buildups appear in the Created wall buildups panel so you can reference them while working
                        elsewhere in the tool. The full Buildups workspace is also stored locally in your browser
                        (local storage) so your work persists between sessions.
                      </p>
                      <p className="text-sm text-black text-justify">
                        If you change the selected stages, the comparison chart updates immediately, and you can click
                        Save info again to refresh the saved wall total with the new stage selection.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">Why this is useful for architects</h4>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                        <li>Compare wall assemblies early without building the entire project model.</li>
                        <li>See which layer drives the most impact and target improvements quickly.</li>
                        <li>Use clear, per m2 numbers to discuss tradeoffs with clients and engineers.</li>
                        <li>Build confidence in material choices even if you are new to sustainability terms.</li>
                      </ul>
                    </div>
                  </section>
                  <div className="border-t border-[#d5dacc]" />
                  <section id="dashboard-lca" className="space-y-3 ">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">LCA</h3>
                    <p className="text-sm text-black text-justify">
                      The LCA tab is where you launch detailed whole building analysis. The Quick LCA card is marked
                      "Coming soon"; use the Whole building LCA option for the full workflow today.
                    </p>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">Whole building LCA</h4>
                      <p className="text-sm text-black text-justify">
                        This workflow turns your design into a component-based model, assigns EPDs to each component,
                        and calculates total impacts and life-cycle stage breakdowns. It is designed so non-technical
                        users can follow it step by step without specialist tools.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-base font-semibold text-black mt-6">Step by step: run a whole building LCA</h4>
                      <ol className="space-y-3">
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 1
                          </div>
                          <div className="text-sm font-semibold text-black">Open the Whole building LCA tool</div>
                          <p className="text-sm text-black text-justify">
                            Go to the Dashboard &gt; LCA tab and click <strong>Launch Whole Building LCA</strong>. This opens
                            the LCA tool in a new workspace.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 2
                          </div>
                          <div className="text-sm font-semibold text-black">Set project settings</div>
                          <p className="text-sm text-black text-justify">
                            At the top of the LCA tool, enter <strong>Building life (years)</strong> and <strong>Building area (m²)</strong>.
                            These values are used to calculate per m²/year results. Toggle <strong>Include module D</strong> if
                            you want to include benefits beyond the system boundary.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 3
                          </div>
                          <div className="text-sm font-semibold text-black">Collect EPD data</div>
                          <p className="text-sm text-black text-justify">
                            Open the <strong>Collect Data</strong> tab to search and shortlist EPDs. Add custom EPDs if a product
                            is missing. Saved wall buildups from the Buildups tab appear here as references when planning
                            your assemblies.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 4
                          </div>
                          <div className="text-sm font-semibold text-black">Build your component list</div>
                          <p className="text-sm text-black text-justify">
                            In the <strong>Build</strong> tab, add components by category (Structure, Envelope, MEP, etc.).
                            For each row, enter a component name, subcategory, quantity, unit, and service life. Choose an
                            EPD from your shortlist. Use <strong>Component actions</strong> to add A4 transport impacts or
                            conversion inputs (density, thickness, custom factor) when required.
                          </p>
                        </li>
                        <li className="relative pl-2 ml-16">
                          <span className="absolute -left-2 top-0 h-full border-l border-[#d5dacc]" aria-hidden="true" />
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step 5
                          </div>
                          <div className="text-sm font-semibold text-black">Review results</div>
                          <p className="text-sm text-black text-justify">
                            Once at least one component is complete, the <strong>Results</strong> tab becomes active. Review
                            totals, category breakdowns, and life-cycle stages. For the most reliable dashboard charts,
                            complete every component you want included in the model.
                          </p>
                        </li>
                      </ol>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-base font-semibold text-black mt-6">What you will see in Results</h4>
                      <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                        <li>Total impacts and intensity per m²/year (when area and life are filled).</li>
                        <li>Breakdowns by category and by life-cycle stage group.</li>
                        <li>Charts that feed the Overview, Breakdown, and Data Visualization tabs on the dashboard.</li>
                      </ul>
                    </div>
                  </section>
                </div>
              </DocCard>
            ) : activeDoc === 'rhino' ? (
              <DocCard>
                <div id="rhino-overview" className="space-y-2">
                  <h2 className="text-2xl font-semibold text-black text-center">Rhino integration</h2>
                  <p className="text-sm text-black text-center">
                    Export your model layers from Rhino and map them to LCA categories inside EmbodiX.
                  </p>
                </div>

                <div className="mt-6 space-y-8">
                  <section id="rhino-requirements" className="space-y-3 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Requirements</h3>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-black text-left">
                      {RHINO_REQUIREMENTS.map((item, idx) => (
                        <li key={`${item.text}-${idx}`}>
                          {item.text}
                          {item.linkText ? (
                            <a href={item.linkHref} className="text-[#2f6046] underline underline-offset-2">
                              {item.linkText}
                            </a>
                          ) : null}
                          {item.tail || ''}
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section id="rhino-file" className="space-y-6 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4 -mb-3">File structure of .embodix</h3>
                    <p className="text-sm text-black text-justify">
                      The export is a list of layer entries. Each entry includes the layer name, geometry type, declared unit
                      (Volume, Area, Length, or Count), and its measured quantity of the declared units from the Rhino model. These values are used to
                      lock the layer name, unit, and quantity in the Build tab and drive the impact calculations.
                    </p>
                    <div className="inline-block overflow-x-auto border border-[#d5dacc] rounded-xl mt-8">
                      <table className="w-auto text-sm">
                        <thead className="bg-[#d8dcd1] text-black">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold">Field</th>
                            <th className="px-4 py-2 text-left font-semibold">Meaning</th>
                          </tr>
                        </thead>
                        <tbody>
                          {RHINO_FILE_FIELDS.map((row) => (
                            <tr key={row.field} className="border-t border-[#eef2e7]">
                              <td className="px-4 py-2 font-semibold text-black">{row.field}</td>
                              <td className="px-4 py-2 text-black">{row.meaning}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section id="rhino-export" className="space-y-3 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Export from Rhino - How do i do it?</h3>
                    <p className="text-sm text-black text-justify">
                      Placeholder: Add step-by-step export instructions for the EmbodiX Rhino plugin. Include where to
                      find the export command, how to select layers, how units are set, and how to save the .embodix
                      file.
                    </p>
                  </section>

                  <section id="rhino-layer-matcher" className="space-y-3 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Layer matcher</h3>
                    <p className="text-sm text-black text-justify">
                      Confirm which LCA category each Rhino layer should belong to.
                    </p>
                    <SimpleList
                      items={RHINO_LAYER_MATCHER_NOTES}
                      className="list-disc space-y-1 pl-5 text-sm text-black"
                    />
                  </section>

                  <section id="rhino-build" className="space-y-3 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Build and results</h3>
                    <p className="text-sm text-black text-justify">
                      Use the Build tab to attach EPDs, then review the results and dashboard charts.
                    </p>
                    <SimpleList items={RHINO_BUILD_NOTES} className="list-disc space-y-1 pl-5 text-sm text-black" />
                  </section>

                  <section id="rhino-workflow" className="space-y-3 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Workflow</h3>
                    <ol className="space-y-3">
                      {RHINO_WORKFLOW_STEPS.map((step, index) => (
                        <li key={step.title} className="relative pl-2 ml-16">
                          <div className="absolute left-[-4rem] top-[2px] text-sm font-semibold text-black">
                            Step {index + 1}
                          </div>
                          <div className="text-sm font-semibold text-black">{step.title}</div>
                          <p className="text-sm text-black text-justify">
                            {typeof step.description === 'string' ? (
                              step.description
                            ) : (
                              <>
                                {step.description.prefix}
                                <a
                                  href={step.description.linkHref}
                                  className="text-[#2f6046] underline underline-offset-2"
                                >
                                  {step.description.linkText}
                                </a>
                                {step.description.suffix}
                              </>
                            )}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </section>

                  <section id="rhino-troubleshooting" className="space-y-3 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-lg font-semibold text-black underline underline-offset-4">Troubleshooting</h3>
                    <SimpleList
                      items={RHINO_TROUBLESHOOTING}
                      className="list-disc space-y-1 pl-5 text-sm text-black text-left"
                    />
                  </section>
                </div>
              </DocCard>
            ) : activeDoc === 'excel' ? (
              <DocCard>
                <div id="excel-overview" className="space-y-2">
                  <h2 className="text-2xl font-semibold text-black text-center">Excel upload template</h2>
                  <p className="text-sm text-black text-center">
                    Follow this structure so the importer can read your spreadsheet without adjustments.
                  </p>
                </div>

                <div className="mt-6 space-y-8">
                  <section id="excel-columns" className="space-y-2 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Required columns</h3>
                    <p className="text-sm text-black">
                      Match the spelling and casing shown below to keep the importer happy.
                    </p>
                    <div className="overflow-x-auto rounded-xl border border-[#d5dacc] bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-[#d8dcd1] text-black">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold">Column</th>
                            <th className="px-4 py-3 text-left font-semibold">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {TEMPLATE_COLUMNS.map((col) => (
                            <tr key={col.title} className="border-t border-[#eef2e7]">
                              <td className="px-4 py-3 font-semibold">{col.title}</td>
                              <td className="px-4 py-3 text-black">{col.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section id="excel-rows" className="space-y-2 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Example rows</h3>
                    <p className="text-sm text-black">
                      Feel free to overwrite these values when you download the template.
                    </p>
                    <div className="overflow-x-auto rounded-2xl border border-[#d5dacc] bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-[#d8dcd1] text-black">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold">Component Name</th>
                            <th className="px-4 py-3 text-left font-semibold">Category</th>
                            <th className="px-4 py-3 text-left font-semibold">GWP</th>
                            <th className="px-4 py-3 text-left font-semibold">AP</th>
                            <th className="px-4 py-3 text-left font-semibold">EP</th>
                            <th className="px-4 py-3 text-left font-semibold">ODP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {SAMPLE_ROWS.map((row) => (
                            <tr key={row.name} className="border-t border-[#eef2e7]">
                              <td className="px-4 py-3">{row.name}</td>
                              <td className="px-4 py-3">{row.category}</td>
                              <td className="px-4 py-3">{row.gwp}</td>
                              <td className="px-4 py-3">{row.ap}</td>
                              <td className="px-4 py-3">{row.ep}</td>
                              <td className="px-4 py-3">{row.odp}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section id="excel-notes" className="space-y-2 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Important notes</h3>
                    <SimpleList items={TEMPLATE_NOTES} className="list-disc space-y-1 pl-5 text-sm text-black" />
                  </section>

                  <section id="excel-download" className="space-y-2 border-b border-[#d5dacc] pb-4">
                    <h3 className="text-2xl font-semibold text-black underline underline-offset-4">Download</h3>
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-[#d5dacc] bg-white px-4 py-5">
                      <div>
                        <p className="text-sm font-semibold text-black">Download starter workbook</p>
                        <p className="text-sm text-black">Coming soon with auto-populated examples.</p>
                      </div>
                      <Button disabled className="bg-[#c8ccc1] text-[#6d756c]">
                        Download (soon)
                      </Button>
                    </div>
                  </section>
                </div>
              </DocCard>
            ) : (
              <DocCard>
                <section id="global-glossary" className="space-y-2">
                  <h2 className="text-2xl font-semibold text-black text-center">Glossary</h2>
                  <p className="text-sm text-black text-center pb-6">
                    Quick definitions for the most common EmbodiX terms.
                  </p>
                  <dl className="mt-4 space-y-3 text-sm text-black text-left">
                    {RHINO_GLOSSARY.map((row) => (
                      <div key={row.term} className="block">
                        <dt className="inline font-semibold">{row.term}:</dt>{' '}
                        <dd className="inline text-black">{row.meaning}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </DocCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;



















